import React from 'react'
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  styled,
  Typography,
  Chip,
  useMediaQuery,
} from '@mui/material'
import lightTheme from '../../styles/theme/lightTheme'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPen,
  faFilePlus,
  faExclamationTriangle,
  faComment,
  faSearch,
  faFile,
  faFileAlt,
  faCode,
  faUser,
  faHeart,
  faCalendarPlus,
} from '@fortawesome/pro-regular-svg-icons'
import { faInfoCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons'
import lock from '../../assets/images/lock.png'
import '../../styles/components/_box.scss'
import { CustomButton, GradientButton } from '../buttons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { hasPermission } from '../../config'
import { acces_depose_annonce, niveauCompetencesList } from '../../data'

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  height: 435,
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  rowGap: '20px',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
    '&::before': {
      opacity: 1,
    },
  },
}))

const ItemLong = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  padding: '24px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.12)',
  },
}))

const BannerCircle = styled(Box)(({ theme }) => ({
  backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  height: 60,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  columnGap: '30px',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.25)',

  '.firstCircle, .secondCircle, .thirdCircle, .fourthCircle, .fiveCircle': {
    width: 10,
    height: 10,
    backgroundColor: lightTheme.palette.secondary.main,
    border: '1px solid #FFFFFF',
    borderRadius: '50%',
  },
}))

const TitleBox = styled(Typography)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 16,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  textTransform: 'uppercase',
  position: 'relative',
  paddingBottom: '8px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '40px',
    height: '3px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '2px',
  },
}))

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const IconContainer = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  border: '2px solid rgba(102, 126, 234, 0.3)',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: 'rgba(102, 126, 234, 0.05)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    border: '2px solid rgba(102, 126, 234, 0.5)',
    transform: 'scale(1.1)',
  },

  '&:active': {
    transform: 'scale(0.95)',
  },
}))

const WrapperIcons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: '8px',
}))

const BannerSquare = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  height: 60,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  columnGap: '30px',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(240, 147, 251, 0.25)',

  '.firstSquare, .secondSquare, .thirdSquare, .fourthSquare, .fiveSquare': {
    width: 10,
    height: 10,
    backgroundColor: '#FFFFFF',
    /*border: "1px solid #FFFFFF",
    borderRadius: "50%"*/
  },
}))

const ValueChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(139, 92, 246, 0.08)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  color: '#8b5cf6',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const UserMasonryLayoutCompany = ({
  user,
  profil,
  besoinClient,
  resumeClient,
  rendezVous,
  offre,
  atelier,
  ged,
  relation,
  valeurs,
  competences,
  softskils,
  type,
  displayValeurSection,
  displayCompetencesSection,
  displaySoftskilSection,
  handleOpenDoc,
  handleOpenBesoin,
  handleOpenResume,
  handleOpenCompetences,
  handleOpenValeurs,
  handleOpenSoftskils,
  handleOpenOffer,
  loading,
  candidatures,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hasPerm = hasPermission(user.data, acces_depose_annonce)

    const sizeChip = useMediaQuery(lightTheme.breakpoints.down('sm')) ? 'small' : 'medium'
  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  return (
    <Container
      maxWidth={'xl'}
      sx={{
        paddingTop: { xs: '16px', sm: '25px' },
        paddingBottom: { xs: '16px', sm: '25px' },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Grid container spacing={{ xs: 2, sm: 3, lg: 4 }}>
        <Grid item xs={12} sm={6} lg={6} order={{ sm: 1, lg: 1 }}>
          <Item
            sx={{
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              padding: { xs: 0, lg: 3 },
            }}
          >
            {type === 'entreprise' ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '60px',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: { xs: 'none', lg: 'flex' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '16px 16px 0 0',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.25)',
                  zIndex: 1,
                }}
              >
                <TitleBox style={{ color: '#fff' }}>
                  {t('profile.entreprise.title')}
                </TitleBox>
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '60px',
                  background:
                    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  display: { xs: 'none', lg: 'flex' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '16px 16px 0 0',
                  boxShadow: '0 8px 32px rgba(240, 147, 251, 0.25)',
                  zIndex: 1,
                }}
              >
                <TitleBox style={{ color: '#fff' }}>
                  {t('profile.candidat.title')}
                </TitleBox>
              </Box>
            )}

            <Box
              sx={{
                pt: { xs: 0, lg: '60px' },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {profil}
            </Box>
          </Item>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          lg={6}
          order={{ sm: 3, lg: 2 }}
          position={'relative'}
        >
          <Item
            sx={{
              height: '100%',
            }}
            className={''}
          >
            <TitleContainer>
              <TitleBox>{t('profile.receivedApplications')}</TitleBox>
            </TitleContainer>
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              {candidatures}
            </Box>
          </Item>
        </Grid>

        {/* Section RDV */}
        <Grid
          item
          xs={12}
          sm={6}
          lg={4}
          order={{ sm: 2, lg: 3 }}
          position={'relative'}
        >
          <Item
            sx={{
              height: '100%',
            }}
          >
            <TitleContainer>
              <TitleBox>{t('profile.rdv')}</TitleBox>
              {/* <IconContainer onClick={() => {}}>
                <FontAwesomeIcon
                  icon={faCalendarPlus}
                  fontSize={15}
                  color={lightTheme.palette.primary.b2}
                />
              </IconContainer> */}
            </TitleContainer>

            <Box>{rendezVous}</Box>
          </Item>
        </Grid>

        {/* Section besoin client */}
        <Grid item xs={12} sm={12} lg={8} order={4}>
          <Item
            sx={{
              height: '100%',
            }}
          >
            <TitleContainer>
              <TitleBox>
                <>{t('profile.jobDescription')}</>
                {!hasPerm && (
                  <Typography
                    variant={'span'}
                    textTransform={'none'}
                    fontSize={10}
                  >
                    <br></br>
                    *Pour que vos fiches soient visibles par les candidats, vous
                    devez mettre à niveau votre offre.
                  </Typography>
                )}
              </TitleBox>
              <WrapperIcons>
                <IconContainer onClick={handleOpenBesoin}>
                  <FontAwesomeIcon
                    icon={faFilePlus}
                    fontSize={15}
                    color={lightTheme.palette.primary.b2}
                  />
                </IconContainer>

                {/* <LinkContainer>
                  <a href='/user/candidats'>
                    Voir tous les candidats{'  '}<FontAwesomeIcon
                      icon={faArrowRight}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    /></a>
                </LinkContainer> */}
              </WrapperIcons>
            </TitleContainer>

            <Box width={'100%'} height={'100%'}>
              {besoinClient}
            </Box>
          </Item>
        </Grid>

        {/* section valeurs */}
        <Grid item xs={12} order={5} display={displayValeurSection}>
          <ItemLong>
            <TitleContainer>
              <TitleBox
                sx={{
                  color:
                    !valeurs || valeurs.length === 0
                      ? lightTheme.palette.error.main
                      : 'initial',
                }}
              >
                {t('filter.values')}
                {(!valeurs || valeurs.length === 0) && '*'}
              </TitleBox>
              <WrapperIcons>
                <IconContainer onClick={handleOpenValeurs}>
                  <FontAwesomeIcon
                    icon={faPen}
                    fontSize={15}
                    color={lightTheme.palette.primary.b2}
                  />
                </IconContainer>
              </WrapperIcons>
            </TitleContainer>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                py: 4,
              }}
            >
              {!valeurs || valeurs.length === 0 ? (
                <Box>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faHeart}
                      fontSize={32}
                      color={lightTheme.palette.error.main}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {t('profile.noValues') || 'Aucune valeur'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {t('profile.addValuesMessage') ||
                      'Ajoutez vos valeurs pour présenter votre culture dentreprise'}
                  </Typography>
                  <CustomButton
                    handleClick={handleOpenValeurs}
                    sx={{
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {t('profile.addValues') || 'Ajouter mes valeurs'}
                  </CustomButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {valeurs &&
                    valeurs.map((valeur, i) => (
                      <ValueChip key={i} label={valeur} size={sizeChip} />
                    ))}
                </Box>
              )}
            </Box>
          </ItemLong>
        </Grid>

        {/* Section GED */}
        <Grid item xs={12} sm={12} lg={12} order={6} position={'relative'}>
          <Item
            sx={{
              height: 280,
            }}
            className={ged ? '' : 'blurLayout'}
          >
            <TitleContainer>
              <TitleBox>{t('profile.ged')}</TitleBox>
              <WrapperIcons>
                <IconContainer onClick={handleOpenDoc}>
                  <FontAwesomeIcon
                    icon={faFilePlus}
                    fontSize={15}
                    color={lightTheme.palette.primary.b2}
                  />
                </IconContainer>
              </WrapperIcons>
            </TitleContainer>

            <Box overflow={'auto'} width={'100%'} height={'100%'}>
              {ged}
            </Box>
          </Item>

          {!ged && (
            <img
              src={lock}
              alt={'Jobertech - not access'}
              style={{
                width: 150,
                height: 150,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-40%, -45%)',
              }}
            />
          )}
        </Grid>

        {/* Section Offres */}
        <Grid item xs={12} sm={12} lg={12} order={7} position={'relative'}>
          <Item
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '20px',
              justifyContent: 'space-between',
            }}
            className={offre ? '' : ''}
          >
            <TitleContainer>
              <TitleBox>{t('profile.purchaseHistory')}</TitleBox>
            </TitleContainer>

            <Box overflow={'auto'} width={'100%'}>
              {offre}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
              }}
            >
              <GradientButton
              
                onClick={() => navigate('/entreprises/offres')}
              >
                {t('profile.submitOfferEntreprise')}
              </GradientButton>
            </Box>
          </Item>
          {/* {!offre && (
            <img
              src={lock}
              alt={'Jobertech - not access'}
              style={{
                width: 150,
                height: 150,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-40%, -45%)',
              }}
            />
          )} */}
        </Grid>

        <Grid item xs={12} sm={12} order={8} position={'relative'}>
          <Item
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '20px',
              justifyContent: 'space-between',
            }}
            className={atelier ? '' : ''}
          >
            <TitleContainer>
              <TitleBox>{t('profile.atelierHistory')}</TitleBox>
            </TitleContainer>

            <Box overflow={'auto'} width={'100%'} height={'100%'}>
              {atelier}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
              }}
            >
              <GradientButton
                displayIcon={'none'}
                onClick={() => {
                  navigate('/ateliers')
                }}
              >
                {t('profile.addAtelier')}
              </GradientButton>
            </Box>
          </Item>
        </Grid>
      </Grid>
    </Container>
  )
}

export default UserMasonryLayoutCompany
